import React, { useState, useEffect, useCallback } from 'react';
import { formatearMoneda, limpiarMoneda, valorCrudo } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';
import { esMexico } from '../utils/calculoRfcCurp';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';

// Estado Civil, Tipo de Identificación y Número de ID ya no se capturan aquí:
// se derivan de lo capturado en el módulo Titular / Coacreditado.
const BANAMEX_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero (a)',
  'Divorciado': 'Divorciado(a)',
  'Viudo': 'Viudo(a)',
  'Union Libre': 'Union Libre',
  'Sociedad de Convivencia': 'Sociedad de Convivencia',
};

function nombreEstadoCivilBanamex(estadoCivil, regimen) {
  if (estadoCivil === 'Casado') {
    if (regimen === 'Sociedad Conyugal') return 'Casado (a) en Sociedad Conyugal';
    if (regimen === 'Separación de Bienes') return 'Casado (a) en Separación de Bienes';
    return null;
  }
  return BANAMEX_ESTADO_CIVIL_MAP[estadoCivil] || null;
}

function idPorNombre(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const stBase = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #e11d48', paddingBottom: 8 },
  subtitle: { fontSize: 15, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  inputNum: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  readOnly: { backgroundColor: '#f3f4f6' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
  saveBtn: { backgroundColor: '#1a3a6e', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  medSiNo: { display: 'flex', gap: 8, alignItems: 'center' },
  aviso: { backgroundColor: '#FFF7E6', border: '1px solid #F5C26B', color: '#7A4B00', padding: '14px 18px', borderRadius: 8, fontSize: 14, marginBottom: 20 },
};

const SiNo = ({ value, onChange, defaultNo }) => {
  useEffect(() => {
    if (defaultNo && (value === undefined || value === null)) onChange(false);
  }, [value, defaultNo, onChange]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (value === undefined || value === null) && defaultNo ? false : value;
  return (
    <div style={stBase.medSiNo}>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === true} onChange={() => onChange(true)} /> Sí
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
};

const BancoBanamex = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const st = {
    ...stBase,
    grid2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 },
    grid3: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 16, marginBottom: 16 },
    grid4: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
    grid6: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: 12, marginBottom: 12 },
  };

  const [escolaridades,        setEscolaridades]        = useState([]);
  const [estadosCiviles,       setEstadosCiviles]        = useState([]);
  const [tiposContrato,        setTiposContrato]         = useState([]);
  const [tiposPersona,         setTiposPersona]          = useState([]);
  const [fuentesIngresos,      setFuentesIngresos]       = useState([]);
  const [destinos,             setDestinos]              = useState([]);
  const [productos,            setProductos]             = useState([]);
  const [tiposProducto,        setTiposProducto]         = useState([]);
  const [destinosVivienda,     setDestinosVivienda]      = useState([]);
  const [plazos,               setPlazos]                = useState([]);
  const [origenesEnganche,     setOrigenesEnganche]      = useState([]);
  const [parentescosCoacr,     setParentescosCoacr]      = useState([]);
  const [estadosInmueble,      setEstadosInmueble]       = useState([]);
  const [ciudadesInmueble,     setCiudadesInmueble]      = useState([]);

  const [bn, setBn] = useState({});
  const [focusedMoneyField, setFocusedMoneyField] = useState(null);
  const [banamexCargado, setBanamexCargado] = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  useEffect(() => {
    if (!prospectoId) { setBanamexCargado(true); return; }
    fetch(`${API}/banamex/${prospectoId}`)
      .then(r => r.ok ? r.json() : null)
      .then(datos => {
        if (datos && datos.id) {
          const montoCredito = parseFloat(datos.cred_monto_credito) || 0;
          const valorInmueble = parseFloat(datos.cred_valor_inmueble) || 0;
          datos = {
            ...datos,
            titular_total: sumaIngresos(datos, 'titular'),
            coac_total: sumaIngresos(datos, 'coac'),
            cred_porcentaje_financiamiento: valorInmueble > 0
              ? String(Math.round((montoCredito / valorInmueble) * 100))
              : '',
          };
          setBn(datos);
          setFormData(prev => ({ ...prev, banamex: datos }));
          if (datos.cred_entidad_inmueble) cargarCiudadesInmueble(datos.cred_entidad_inmueble);
        }
      })
      .catch(e => console.error('Error cargando datos Banamex:', e))
      .finally(() => setBanamexCargado(true));
  }, [prospectoId]);

  // Estado Civil, Tipo de Identificación y Número de ID: se derivan de Titular /
  // Coacreditado. Se recalculan cuando cambian ahí, y también una vez que
  // terminó de cargar lo guardado de Banamex, para no quedar pisados por un
  // valor anterior guardado en la base de datos.
  useEffect(() => {
    if (!banamexCargado) return;
    if (estadosCiviles.length === 0) return;

    const ecIdTit = idPorNombre(estadosCiviles, nombreEstadoCivilBanamex(formData.titular.estado_civil, formData.titular.regimen_matrimonio));
    const ecIdCoac = idPorNombre(estadosCiviles, nombreEstadoCivilBanamex(formData.coacreditado?.estado_civil, formData.coacreditado?.regimen_matrimonio));

    setBn(prev => {
      const next = { ...prev };
      let changed = false;
      const set = (k, v) => { if (next[k] !== v) { next[k] = v; changed = true; } };
      set('titular_estado_civil_id', ecIdTit);
      set('titular_tipo_identificacion', formData.titular.tipo_identificacion || '');
      set('titular_num_id', formData.titular.numero_identificacion || '');
      set('coac_estado_civil_id', ecIdCoac);
      set('coac_tipo_identificacion', formData.coacreditado?.tipo_identificacion || '');
      set('coac_num_id', formData.coacreditado?.numero_identificacion || '');
      return changed ? next : prev;
    });
  }, [
    banamexCargado, estadosCiviles,
    formData.titular.estado_civil, formData.titular.regimen_matrimonio,
    formData.titular.tipo_identificacion, formData.titular.numero_identificacion,
    formData.coacreditado?.estado_civil, formData.coacreditado?.regimen_matrimonio,
    formData.coacreditado?.tipo_identificacion, formData.coacreditado?.numero_identificacion,
  ]);

  // Selección Tipo Seguros: Vida Saldo Insoluto y Vida Monto Original son
  // complementarios — se captura Saldo Insoluto y Monto Original se deriva
  // como el valor opuesto, en solo lectura (sin poder modificarse a mano).
  useEffect(() => {
    if (bn.seg_vida_saldo_insoluto === undefined || bn.seg_vida_saldo_insoluto === null) return;
    const opuesto = !bn.seg_vida_saldo_insoluto;
    if (bn.seg_vida_monto_original !== opuesto) {
      setBn(prev => ({ ...prev, seg_vida_monto_original: opuesto }));
    }
  }, [bn.seg_vida_saldo_insoluto]); // eslint-disable-line react-hooks/exhaustive-deps

  const sumaIngresos = (datos, prefix) =>
    ((parseFloat(datos[`${prefix}_sueldo_bruto`]) || 0)
    + (parseFloat(datos[`${prefix}_sueldo_variable`]) || 0)
    + (parseFloat(datos[`${prefix}_ingresos_independiente`]) || 0)).toFixed(2);

  const cargarCatalogos = async () => {
    try {
      const cats = await Promise.all([
        fetch(`${API}/catalogos/banamex/escolaridad`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/estado_civil`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/tipo_contrato`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/tipo_persona`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/fuente_ingresos`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/destino`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/producto`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/tipo_producto`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/destino_vivienda`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/plazo_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/origen_enganche`).then(r => r.json()),
        fetch(`${API}/catalogos/banamex/parentesco_coacreditado`).then(r => r.json()),
        fetch(`${API}/sepomex/estados`).then(r => r.json()),
      ]);
      setEscolaridades(cats[0]);     setEstadosCiviles(cats[1]);
      setTiposContrato(cats[2]);     setTiposPersona(cats[3]);
      setFuentesIngresos(cats[4]);   setDestinos(cats[5]);
      setProductos(cats[6]);         setTiposProducto(cats[7]);
      setDestinosVivienda(cats[8]);  setPlazos(cats[9]);
      setOrigenesEnganche(cats[10]); setParentescosCoacr(cats[11]);
      setEstadosInmueble((cats[12] || []).map(r => r.estado || r));
    } catch (e) {
      console.error('Error cargando catálogos Banamex:', e);
    }
  };

  const cargarCiudadesInmueble = async (estado) => {
    if (!estado) { setCiudadesInmueble([]); return; }
    try {
      const r = await fetch(`${API}/sepomex/municipios/${encodeURIComponent(estado)}`);
      const d = await r.json();
      setCiudadesInmueble(d.map(m => m.municipio || m));
    } catch (e) { console.error('Error cargando ciudades inmueble:', e); }
  };

  const upd = useCallback((campo, valor) => {
    setBn(prev => {
      const next = { ...prev, [campo]: valor };
      if (['titular_sueldo_bruto','titular_sueldo_variable','titular_ingresos_independiente'].includes(campo)) {
        next.titular_total = sumaIngresos(next, 'titular');
      }
      if (['coac_sueldo_bruto','coac_sueldo_variable','coac_ingresos_independiente'].includes(campo)) {
        next.coac_total = sumaIngresos(next, 'coac');
      }
      if (['cred_monto_credito','cred_valor_inmueble'].includes(campo)) {
        const montoCredito = parseFloat(next.cred_monto_credito) || 0;
        const valorInmueble = parseFloat(next.cred_valor_inmueble) || 0;
        next.cred_porcentaje_financiamiento = valorInmueble > 0
          ? String(Math.round((montoCredito / valorInmueble) * 100))
          : '';
      }
      return next;
    });
  }, []);

  const guardar = async () => {
    if (!prospectoId) {
      alert('Primero guarde el prospecto antes de agregar información del banco');
      return;
    }
    if (creditoExcedeInmueble(bn.cred_monto_credito, bn.cred_valor_inmueble)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    try {
      const r = await fetch(`${API}/banamex/${prospectoId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospecto_id: prospectoId, ...bn }),
      });
      const data = await r.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, banamex: bn }));
        alert('✓ Información de Banamex guardada correctamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (e) {
      alert('Error al guardar la información');
    }
  };

  const tieneCoac = !!formData.coacreditado?.nombres;
  const coacParticipaIngresos = formData.coacreditado?.participa_ingresos === true;
  // Misma regla que coacActivo() del PDF (bancos/impresion/banamex.js). Si no
  // participa con ingresos, Banamex solo usa Nombre, RFC y CURP de la pestaña
  // Coacreditado: sus secciones se ocultan (desmontadas, así los SiNo con
  // defaultNo no se disparan) y lo ya guardado se conserva en `bn` y se reenvía
  // tal cual al guardar.
  const coacActivo = tieneCoac && coacParticipaIngresos;
  // Caso B (misma regla que conyugeAparte() del PDF): coacreditado que no es la cónyuge y titular Casado en Sociedad Conyugal →
  // se piden los datos de la esposa (banamex_info.conyuge_*). Si la condición deja de cumplirse la tarjeta se oculta, pero lo
  // capturado se conserva en `bn` y se reenvía tal cual al guardar.
  const pideConyuge = tieneCoac && formData.coacreditado?.es_conyuge !== true
    && formData.titular?.estado_civil === 'Casado' && formData.titular?.regimen_matrimonio === 'Sociedad Conyugal';
  const conyugeIncompleta = pideConyuge && !(String(bn.conyuge_nombre || '').trim() && String(bn.conyuge_apellido_paterno || '').trim());
  const esAdquisicion = parseInt(bn.cred_destino_id) === 1;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const renderSiNo = (campo) => (
    <select
      value={bn[campo] === true ? 'true' : bn[campo] === false ? 'false' : ''}
      onChange={e => upd(campo, e.target.value === '' ? null : e.target.value === 'true')}
      style={st.input}
    >
      <option value="">-- Seleccionar --</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>
  );

  const renderSelect = (campo, opciones) => (
    <select value={bn[campo] || ''} onChange={e => upd(campo, e.target.value)} style={st.input}>
      <option value="">-- Seleccionar --</option>
      {opciones.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
    </select>
  );

  const renderMoney = (label, campo, decimales = 0) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <div style={st.moneyWrap}>
        <span style={st.moneySign}>$</span>
        <input type="text" inputMode="decimal"
          value={focusedMoneyField === campo ? valorCrudo(bn[campo], decimales) : formatearMoneda(bn[campo], decimales)}
          onFocus={() => setFocusedMoneyField(campo)}
          onBlur={() => setFocusedMoneyField(null)}
          onChange={e => upd(campo, limpiarMoneda(e.target.value, decimales))}
          style={{ ...st.inputNum, paddingLeft: 24 }} placeholder={decimales === 0 ? '0' : '0.00'} />
      </div>
    </div>
  );

  const renderMoneyRO = (label, campo, decimales = 0) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <input readOnly
        value={`$${formatearMoneda(bn[campo] || 0, decimales)}`}
        style={{ ...st.input, ...st.readOnly }} />
    </div>
  );

  // ── Sección: Info General ────────────────────────────────────────────────────
  const renderInfoGeneral = (prefix, titulo) => (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>
      <div style={st.grid4}>
        <div style={st.field}>
          <label style={st.label}>¿Es Cliente Banamex?</label>
          {renderSiNo(`${prefix}_cliente_banamex`)}
        </div>
        <div style={st.field}>
          <label style={st.label}>Número de Cliente Banamex</label>
          <input type="number" value={bn[`${prefix}_num_cliente`] || ''}
            onChange={e => upd(`${prefix}_num_cliente`, e.target.value)} style={st.inputNum} />
        </div>
        <div style={st.field}>
          <label style={st.label}>Estado Civil</label>
          <input readOnly
            value={estadosCiviles.find(ec => ec.id === bn[`${prefix}_estado_civil_id`])?.nombre || ''}
            style={{ ...st.input, ...st.readOnly }}
            title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        <div style={st.field}>
          <label style={st.label}>Escolaridad</label>
          {renderSelect(`${prefix}_escolaridad_id`, escolaridades)}
        </div>
      </div>
      <div style={st.grid3}>
        <div style={st.field}>
          <label style={st.label}>Tipo de Identificación</label>
          <input readOnly value={bn[`${prefix}_tipo_identificacion`] || ''}
            style={{ ...st.input, ...st.readOnly }}
            title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        <div style={st.field}>
          <label style={st.label}>Número de ID</label>
          <input readOnly value={bn[`${prefix}_num_id`] || ''}
            style={{ ...st.input, ...st.readOnly }}
            title="Se captura en la pestaña Titular / Coacreditado" />
        </div>
        {!esMexico((prefix === 'coac' ? formData.coacreditado : formData.titular)?.pais_nacimiento) && (
          <div style={st.field}>
            <label style={st.label}>Años de Residencia en México</label>
            <input type="number" maxLength={2} value={bn[`${prefix}_anios_residencia`] || ''}
              onChange={e => upd(`${prefix}_anios_residencia`, e.target.value)} style={st.inputNum} />
          </div>
        )}
      </div>
      <div style={st.grid2}>
        <div style={st.field}>
          <label style={st.label}>Dependientes Económicos (parentesco, edad, ocupación)</label>
          <input value={bn[`${prefix}_dependientes_info`] || ''}
            onChange={e => upd(`${prefix}_dependientes_info`, e.target.value)} style={st.input}
            placeholder="Ej: Hijo 5 años estudiante, Madre 60 años pensionada" />
        </div>
      </div>
      {prefix === 'coac' && (
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>Parentesco con el Solicitante</label>
            {renderSelect('coac_parentesco_id', parentescosCoacr)}
          </div>
          <div style={{ maxWidth: isMobile ? '100%' : '70%' }}>
            <div style={st.field}>
              <label style={st.label}>¿Participa en Crédito Infonavit?</label>
              {renderSiNo('coac_participa_infonavit')}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Sección: Información de Trabajo ─────────────────────────────────────────
  const renderTrabajo = (prefix, titulo) => {
    const tipoPersonaId = parseInt(bn[`${prefix}_tipo_persona_id`]);
    const esAsalariado   = tipoPersonaId === 1;
    const esIndependiente = tipoPersonaId === 2;
    const esMixto        = tipoPersonaId === 3;
    return (
      <div style={st.card}>
        <h3 style={st.title}>{titulo}</h3>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Tipo de Persona</label>
            {renderSelect(`${prefix}_tipo_persona_id`, tiposPersona)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Tipo de Contrato</label>
            {renderSelect(`${prefix}_tipo_contrato_id`, tiposContrato)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Puesto</label>
            <input value={bn[`${prefix}_puesto`] || ''}
              onChange={e => upd(`${prefix}_puesto`, e.target.value)} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Ocupación / Profesión</label>
            <input value={bn[`${prefix}_ocupacion`] || ''}
              onChange={e => upd(`${prefix}_ocupacion`, e.target.value)} style={st.input} />
          </div>
        </div>
        <div style={st.grid2}>
          <div style={{ maxWidth: isMobile ? '100%' : '70%' }}>
            <div style={st.field}>
              <label style={st.label}>Actividad / Giro de Negocio</label>
              <input value={bn[`${prefix}_actividad_giro`] || ''}
                onChange={e => upd(`${prefix}_actividad_giro`, e.target.value)} style={st.input} />
            </div>
          </div>
        </div>
        <div style={st.grid4}>
          {(esAsalariado || esMixto) && (
            <div style={{ maxWidth: isMobile ? '100%' : '75%' }}>{renderMoney('Sueldo Mensual Bruto', `${prefix}_sueldo_bruto`)}</div>
          )}
          {(esAsalariado || esMixto) && (
            <div style={{ maxWidth: isMobile ? '100%' : '75%' }}>{renderMoney('Sueldo Mensual Variable', `${prefix}_sueldo_variable`)}</div>
          )}
          {(esIndependiente || esMixto) &&
            renderMoney('Ingresos Independiente Mensual', `${prefix}_ingresos_independiente`)}
          <div style={{ maxWidth: isMobile ? '100%' : '75%' }}>{renderMoneyRO('Total de Ingresos Mensuales', `${prefix}_total`)}</div>
        </div>
        {(esIndependiente || esMixto) && (
          <div style={st.grid2}>
            <div style={st.field}>
              <label style={st.label}>Fuente de Ingresos Independientes</label>
              {renderSelect(`${prefix}_fuente_ingresos_id`, fuentesIngresos)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Sección: Selección Tipo Seguros ──────────────────────────────────────────
  const renderSeleccionSeguros = () => (
    <div style={st.card}>
      <h3 style={st.title}>Selección Tipo Seguros</h3>
      <div style={st.grid3}>
        <div style={st.field}>
          <label style={st.label}>Seguro de Vida Saldo Insoluto</label>
          {renderSiNo('seg_vida_saldo_insoluto')}
        </div>
        <div style={st.field}>
          <label style={st.label}>Seguro de Vida Monto Original</label>
          <input readOnly
            value={bn.seg_vida_monto_original === true ? 'Sí' : bn.seg_vida_monto_original === false ? 'No' : ''}
            style={{ ...st.input, ...st.readOnly }}
            title="Se calcula automáticamente a partir de Seguro de Vida Saldo Insoluto" />
        </div>
        <div style={st.field}>
          <label style={st.label}>Seguro de Daños</label>
          {renderSiNo('seg_danos')}
        </div>
      </div>
    </div>
  );

  // ── Sección: Cuestionario Seguros ────────────────────────────────────────────
  const renderSeguros = (prefix, titulo) => (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>
      <div style={st.grid2}>
        <div style={st.field}>
          <label style={st.label}>Profesión u Ocupación (detalle)</label>
          <input value={bn[`seg_${prefix}_profesion_detalle`] || ''}
            onChange={e => upd(`seg_${prefix}_profesion_detalle`, e.target.value)} style={st.input} />
        </div>
      </div>
      <div style={st.grid2}>
        <div style={st.field}>
          <label style={st.label}>¿Practica deportes de alto riesgo?</label>
          <SiNo value={bn[`seg_${prefix}_deportes_riesgo`]} onChange={v => upd(`seg_${prefix}_deportes_riesgo`, v)} defaultNo />
        </div>
        {bn[`seg_${prefix}_deportes_riesgo`] === true && (
          <div style={st.field}>
            <label style={st.label}>Especifique deporte(s)</label>
            <input value={bn[`seg_${prefix}_deportes_especifica`] || ''}
              onChange={e => upd(`seg_${prefix}_deportes_especifica`, e.target.value)} style={st.input} />
          </div>
        )}
      </div>
      <div style={st.grid2}>
        <div style={st.field}>
          <label style={st.label}>¿Padece enfermedades graves?</label>
          <SiNo value={bn[`seg_${prefix}_enfermedades`]} onChange={v => upd(`seg_${prefix}_enfermedades`, v)} defaultNo />
        </div>
        {bn[`seg_${prefix}_enfermedades`] === true && (
          <div style={st.field}>
            <label style={st.label}>Especifique enfermedad(es)</label>
            <input value={bn[`seg_${prefix}_enfermedades_especifica`] || ''}
              onChange={e => upd(`seg_${prefix}_enfermedades_especifica`, e.target.value)} style={st.input} />
          </div>
        )}
      </div>
    </div>
  );

  // ── Sección: Beneficiarios ────────────────────────────────────────────────────
  const renderBeneficiarios = (prefix, titulo) => {
    const mostrar = bn.seg_vida_monto_original === true && (prefix === 'tit' || (prefix === 'coac' && tieneCoac));
    if (!mostrar) return null;
    return (
      <div style={st.card}>
        <h3 style={st.title}>{titulo}</h3>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 12, marginBottom: 12 }}>
            <p style={st.subtitle}>Beneficiario {i}</p>
            <div style={st.grid3}>
              <div style={st.field}>
                <label style={st.label}>Nombre Completo</label>
                <input value={bn[`ben_${prefix}_${i}_nombre`] || ''}
                  onChange={e => upd(`ben_${prefix}_${i}_nombre`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Domicilio</label>
                <input value={bn[`ben_${prefix}_${i}_domicilio`] || ''}
                  onChange={e => upd(`ben_${prefix}_${i}_domicilio`, e.target.value)} style={st.input} />
              </div>
              <div style={st.field}>
                <label style={st.label}>Teléfono (10 dígitos)</label>
                <input type="number" value={bn[`ben_${prefix}_${i}_telefono`] || ''}
                  onChange={e => upd(`ben_${prefix}_${i}_telefono`, e.target.value)} style={st.inputNum} />
              </div>
            </div>
            <div style={st.grid3}>
              <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
                <div style={st.field}>
                  <label style={st.label}>Porcentaje (%)</label>
                  <input type="number" value={bn[`ben_${prefix}_${i}_porcentaje`] || ''}
                    onChange={e => upd(`ben_${prefix}_${i}_porcentaje`, e.target.value)} style={st.inputNum} placeholder="Sin decimales" />
                </div>
              </div>
              <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
                <div style={st.field}>
                  <label style={st.label}>Parentesco</label>
                  <input value={bn[`ben_${prefix}_${i}_parentesco`] || ''}
                    onChange={e => upd(`ben_${prefix}_${i}_parentesco`, e.target.value)} style={st.input} />
                </div>
              </div>
              <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
                <div style={st.field}>
                  <label style={st.label}>Fecha de Nacimiento</label>
                  <input type="date" value={bn[`ben_${prefix}_${i}_fecha_nacimiento`] || ''}
                    onChange={e => upd(`ben_${prefix}_${i}_fecha_nacimiento`, e.target.value)} style={st.input} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* INFO GENERAL TITULAR */}
      {renderInfoGeneral('titular', 'Información General del Titular')}

      {/* TRABAJO TITULAR */}
      {renderTrabajo('titular', 'Información del Trabajo del Titular')}

      {/* INFO GENERAL COACREDITADO */}
      {coacActivo && renderInfoGeneral('coac', 'Información General del Coacreditado')}

      {/* COACREDITADO SIN INGRESOS: solo aviso */}
      {tieneCoac && !coacActivo && (
        <div style={st.aviso}>
          El coacreditado no participa con ingresos: para Banamex solo se usan Nombre, RFC y CURP
          capturados en la pestaña Coacreditado.
        </div>
      )}

      {/* TRABAJO COACREDITADO */}
      {coacActivo && renderTrabajo('coac', 'Información del Trabajo del Coacreditado')}

      {/* SELECCIÓN TIPO SEGUROS */}
      {renderSeleccionSeguros()}

      {/* SEGUROS TITULAR */}
      {renderSeguros('tit', 'Cuestionario de Seguros del Titular')}

      {/* BENEFICIARIOS TITULAR */}
      {renderBeneficiarios('tit', 'Beneficiarios del Titular')}

      {/* SEGUROS COACREDITADO */}
      {coacActivo && renderSeguros('coac', 'Cuestionario de Seguros del Coacreditado')}

      {/* BENEFICIARIOS COACREDITADO */}
      {coacActivo && renderBeneficiarios('coac', 'Beneficiarios del Coacreditado')}

      {/* CÓNYUGE DEL TITULAR (caso B) */}
      {pideConyuge && (
        <div style={st.card}>
          <h3 style={st.title}>Datos de la Cónyuge del Titular</h3>
          <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
            El titular está casado en Sociedad Conyugal y el coacreditado no es su cónyuge. Estos datos se imprimen en la
            primera línea de la hoja 2 y en la firma "Cónyuge del Solicitante" de la hoja 3.
          </p>
          {conyugeIncompleta && (
            <div style={st.aviso}>Faltan el nombre y el apellido paterno de la cónyuge: mientras no se capturen, la solicitud se imprime como hasta ahora.</div>
          )}
          <div style={st.grid3}>
            {[['conyuge_nombre', 'Nombre(s)', 150], ['conyuge_apellido_paterno', 'Apellido Paterno', 100], ['conyuge_apellido_materno', 'Apellido Materno', 100]].map(([k, l, max]) => (
              <div key={k} style={st.field}>
                <label style={st.label}>{l}</label>
                <input value={bn[k] || ''} maxLength={max} onChange={e => upd(k, e.target.value)} style={st.input} />
              </div>
            ))}
          </div>
          <div style={st.grid3}>
            {[['conyuge_rfc', 'RFC', 13], ['conyuge_curp', 'CURP', 18]].map(([k, l, max]) => (
              <div key={k} style={st.field}>
                <label style={st.label}>{l}</label>
                <input value={bn[k] || ''} maxLength={max} onChange={e => upd(k, e.target.value.toUpperCase().replace(/\s/g, ''))} style={st.input} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CRÉDITO */}
      <div style={st.card}>
        <h3 style={st.title}>Información del Crédito</h3>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Oferta Vinculante</label>
            {renderSiNo('cred_oferta_vinculante')}
          </div>
          <div style={st.field}>
            <label style={st.label}>Producto</label>
            {renderSelect('cred_producto_id', productos)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Destino</label>
            {renderSelect('cred_destino_id', destinos)}
          </div>
          {esAdquisicion && (
            <div style={st.field}>
              <label style={st.label}>Tipo de Producto</label>
              {renderSelect('cred_tipo_producto_id', tiposProducto)}
            </div>
          )}
        </div>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Destino de la Vivienda</label>
            {renderSelect('cred_destino_vivienda_id', destinosVivienda)}
          </div>
          {esAdquisicion && (
            <div style={st.field}>
              <label style={st.label}>Origen del Enganche</label>
              {renderSelect('cred_origen_enganche_id', origenesEnganche)}
            </div>
          )}
          <div style={st.field}>
            <label style={st.label}>Plazo</label>
            {renderSelect('cred_plazo_id', plazos)}
          </div>
        </div>
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>Tipo de Inmueble</label>
            <select value={bn.cred_tipo_inmueble || ''} onChange={e => upd('cred_tipo_inmueble', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="Casa">Casa</option>
              <option value="Departamento">Departamento</option>
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Entidad Federativa del Inmueble</label>
            <select
              value={bn.cred_entidad_inmueble || ''}
              onChange={e => {
                upd('cred_entidad_inmueble', e.target.value);
                upd('cred_ciudad_inmueble', '');
                cargarCiudadesInmueble(e.target.value);
              }}
              style={st.input}
            >
              <option value="">-- Seleccionar --</option>
              {estadosInmueble.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Ciudad del Inmueble</label>
            <select
              value={bn.cred_ciudad_inmueble || ''}
              onChange={e => upd('cred_ciudad_inmueble', e.target.value)}
              style={st.input}
            >
              <option value="">-- Seleccionar --</option>
              {ciudadesInmueble.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={st.grid4}>
          {renderMoney('Monto del Crédito Solicitado', 'cred_monto_credito')}
          {renderMoney('Valor Estimado del Inmueble', 'cred_valor_inmueble')}
          <div style={st.field}>
            <label style={st.label}>Tasa de Interés (%)</label>
            <input type="number" step="0.01" value={bn.cred_tasa_interes || ''}
              onChange={e => upd('cred_tasa_interes', e.target.value)} style={st.inputNum} placeholder="0.00" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Porcentaje de Financiamiento (%)</label>
            <input readOnly value={bn.cred_porcentaje_financiamiento || ''}
              style={{ ...st.inputNum, ...st.readOnly }} placeholder="Se calcula automáticamente" />
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <button onClick={guardar} style={st.saveBtn}>Guardar Información Banamex</button>
      </div>
    </div>
  );
};

export default BancoBanamex;
